import React, { useState, useEffect } from 'react';
import { Sun, Moon, Menu, X, Languages, Settings, LogIn, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface HeaderProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const Header: React.FC<HeaderProps> = ({ activeTab, setActiveTab }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, isAdmin, signOut } = useAuth();
  
  useEffect(() => {
    const storedDarkMode = localStorage.getItem('dark-mode');
    
    if (storedDarkMode === 'true') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    } else if (storedDarkMode === 'false') {
      setIsDarkMode(false);
      document.documentElement.classList.remove('dark');
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(prefersDark);
      prefersDark
        ? document.documentElement.classList.add('dark')
        : document.documentElement.classList.remove('dark');
    }
  }, []);
  
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    if (isDarkMode) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('dark-mode', 'false');
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('dark-mode', 'true');
    }
  };

  const handleLogout = async () => {
    await signOut();
    setActiveTab('text');
  };
  
  const navItems = [
    { id: 'text', label: 'Text' },
    { id: 'document', label: 'Document' },
    { id: 'website', label: 'Website' },
    { id: 'phrasebook', label: 'Phrasebook' },
    { id: 'history', label: 'History' }
  ];

  if (isAuthenticated && isAdmin) {
    navItems.push({ 
      id: 'admin', 
      label: 'Admin Dashboard', 
      icon: Settings 
    });
  }
  
  return (
    <header className="bg-white dark:bg-gray-800 shadow sticky top-0 z-10 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <Languages className="h-8 w-8 text-[#00A86B]" />
              <div className="ml-2 flex flex-col items-start">
                <span className="text-sm font-medium text-[#00A86B]">JSB-Tech LLC</span>
                <span className="text-xl font-bold text-primary dark:text-primary-dark">TranslateNow</span>
              </div>
            </Link>
            
            <nav className="hidden sm:ml-6 sm:flex sm:space-x-4">
              {navItems.map(item => (
                <Link
                  key={item.id}
                  to={`/${item.id === 'text' ? '' : item.id}`}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    activeTab === item.id
                      ? 'bg-[#00A86B] bg-opacity-10 text-[#00A86B]'
                      : 'text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700'
                  } transition-colors duration-200 flex items-center`}
                  onClick={() => setActiveTab(item.id)}
                >
                  {item.icon && <item.icon className="h-4 w-4 mr-2" />}
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
          
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="ml-4 px-3 py-2 rounded-md text-sm font-medium text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            ) : (
              <Link
                to="/auth"
                className="ml-4 px-3 py-2 rounded-md text-sm font-medium text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In / Register
              </Link>
            )}
            <button
              type="button"
              onClick={toggleDarkMode}
              className="ml-4 p-2 rounded-full text-primary dark:text-primary-dark hover:text-[#1E90FF] dark:hover:text-[#1E90FF] focus:outline-none transition-colors duration-200"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>
          </div>
          
          <div className="flex items-center sm:hidden">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-primary dark:text-primary-dark hover:text-primary-dark dark:hover:text-primary-light focus:outline-none"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {isMobileMenuOpen && (
        <div className="sm:hidden bg-white dark:bg-gray-800 shadow-md">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map(item => (
              <Link
                key={item.id}
                to={`/${item.id === 'text' ? '' : item.id}`}
                className={`block px-3 py-2 rounded-md text-base font-medium w-full text-left ${
                  activeTab === item.id
                    ? 'bg-[#00A86B] bg-opacity-10 text-[#00A86B]'
                    : 'text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700'
                } transition-colors duration-200 flex items-center`}
                onClick={() => {
                  setActiveTab(item.id);
                  setIsMobileMenuOpen(false);
                }}
              >
                {item.icon && <item.icon className="h-4 w-4 mr-2" />}
                {item.label}
              </Link>
            ))}
            
            {isAuthenticated ? (
              <button
                onClick={() => {
                  handleLogout();
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full px-3 py-2 rounded-md text-base font-medium text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700 text-left flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            ) : (
              <Link
                to="/auth"
                className="block px-3 py-2 rounded-md text-base font-medium text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In / Register
              </Link>
            )}
            
            <button
              type="button"
              onClick={() => {
                toggleDarkMode();
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center w-full px-3 py-2 rounded-md text-base font-medium text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {isDarkMode ? (
                <>
                  <Sun className="h-5 w-5 mr-2" />
                  Light Mode
                </>
              ) : (
                <>
                  <Moon className="h-5 w-5 mr-2" />
                  Dark Mode
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;